import React from 'react';
import { Document, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';

export type ReportColumn = {
  header: string;
  key: string;
  flex?: number;
  align?: 'left' | 'center' | 'right';
};

export type ReportRow = Record<string, string>;

export type ChartImage = {
  title: string;
  description?: string;
  imageSrc: string; // base64 data URL
};

interface A4ReportDocumentProps {
  title: string;
  subtitle?: string;
  filterLabel?: string;
  filterValue?: string;
  filterTagVariant?: 'active' | 'archived';
  /**
   * Optional header branding block (right-aligned), matching the app header:
   * Alea logo + divider + label + Seni Smart logo.
   */
  headerRightLeadingLogo?: { src: string; width?: number; height?: number };
  /**
   * Use this for SVG logos rendered via @react-pdf/renderer Svg components.
   * Prefer this over Image for SVGs.
   */
  headerRightTrailing?: React.ReactNode;
  headerRightLogos?: Array<{ src: string; width: number; height: number }>;
  headerRightLabel?: string;
  columns: ReportColumn[];
  rows: ReportRow[];
  organisationName?: string;
  exportedAtLabel: string;
  exportedAtValue: string;
  pageLabel: string; // e.g. "Page"
  /**
   * Optional chart images to include in the PDF
   */
  charts?: ChartImage[];
  /**
   * Use compact table style (for analysis page)
   */
  compactTable?: boolean;
}

const styles = StyleSheet.create({
  page: {
    paddingTop: 28,
    paddingBottom: 36,
    paddingHorizontal: 28,
    fontSize: 10,
    color: '#2B4956',
    fontFamily: 'Helvetica',
  },
  chartSection: {
    marginTop: 20,
    marginBottom: 20,
  },
  chartTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: '#2B4956',
    marginBottom: 4,
  },
  chartDescription: {
    fontSize: 11,
    color: '#5B4956',
    marginBottom: 12,
    paddingLeft: 0,
    lineHeight: 1.2,
  },
  chartDescriptionParenthetical: {
    fontSize: 8,
    fontWeight: 300,
    color: '#5B4956',
    lineHeight: 1.2,
  },
  chartImage: {
    width: '100%',
    marginBottom: 20,
  },
  header: {
    marginBottom: 14,
  },
  headerTopRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
  },
  headerLeft: {
    flexGrow: 1,
  },
  headerRight: {
    flexShrink: 0,
    alignItems: 'flex-end',
  },
  headerBrandRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  headerDivider: {
    width: 1,
    height: 28,
    backgroundColor: '#DCDCDC',
  },
  headerRightLabel: {
    fontSize: 8,
    color: '#5B6467',
    maxWidth: 70,
    textAlign: 'right',
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    color: '#2B4956',
  },
  subtitle: {
    marginTop: 4,
    fontSize: 10,
    color: '#5B6467',
  },
  filterRow: {
    marginTop: 16,
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 6,
  },
  filterTag: {
    borderRadius: 25,
    borderWidth: 1,
    borderStyle: 'solid',
    paddingHorizontal: 14,
    paddingVertical: 6,
  },
  filterTagText: {
    fontSize: 12,
    fontWeight: 700,
  },
  filterValue: {
    fontSize: 12,
    color: '#2B4956',
    marginLeft: 8,
  },
  table: {
    borderWidth: 1,
    borderColor: '#DCDCDC',
    borderStyle: 'solid',
    borderRadius: 6,
    overflow: 'hidden',
  },
  tableCompact: {
    overflow: 'hidden',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#F3F2F2',
    borderBottomWidth: 1,
    borderBottomColor: '#DCDCDC',
    borderBottomStyle: 'solid',
  },
  tableHeaderCompact: {
    flexDirection: 'row',
    backgroundColor: '#FFFFFF',
  },
  tableHeaderCell: {
    paddingVertical: 8,
    paddingHorizontal: 10,
    fontSize: 10,
    fontWeight: 700,
    color: '#2B4956',
  },
  tableHeaderCellCompact: {
    paddingVertical: 4,
    paddingLeft: 0,
    paddingRight: 10,
    fontSize: 8,
    fontWeight: 'normal',
    color: '#2B4956',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#EEEEEE',
    borderBottomStyle: 'solid',
  },
  tableRowCompact: {
    flexDirection: 'row',
  },
  tableCell: {
    paddingVertical: 8,
    paddingHorizontal: 10,
    fontSize: 10,
    color: '#2B4956',
  },
  tableCellCompact: {
    paddingVertical: 4,
    paddingLeft: 0,
    paddingRight: 10,
    fontSize: 10,
    fontWeight: 400,
    color: '#2B4956',
  },
  footer: {
    position: 'absolute',
    left: 28,
    right: 28,
    bottom: 18,
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    fontSize: 9,
    color: '#5B6467',
  },
  footerLeft: {
    width: '70%',
  },
  footerLeftOrg: {
    fontSize: 9,
    color: '#5B6467',
  },
  footerLeftExport: {
    marginTop: 2,
    fontSize: 9,
    color: '#5B6467',
  },
  footerRight: {
    width: '30%',
    textAlign: 'right',
  },
});

function getTextAlign(align?: 'left' | 'center' | 'right') {
  if (align === 'center') return 'center';
  if (align === 'right') return 'right';
  return 'left';
}

export const A4ReportDocument: React.FC<A4ReportDocumentProps> = ({
  title,
  subtitle,
  filterLabel,
  filterValue,
  filterTagVariant = 'active',
  headerRightLeadingLogo,
  headerRightTrailing,
  headerRightLogos,
  headerRightLabel,
  columns,
  rows,
  organisationName,
  exportedAtLabel,
  exportedAtValue,
  pageLabel,
  charts,
  compactTable = false,
}) => {
  const totalFlex = columns.reduce((acc, col) => acc + (col.flex ?? 1), 0);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <View style={styles.headerTopRow}>
            <View style={styles.headerLeft}>
              <Text style={styles.title}>{title}</Text>
              {!!subtitle && <Text style={styles.subtitle}>{subtitle}</Text>}
            </View>

            {(headerRightLeadingLogo || headerRightTrailing || headerRightLogos?.length || headerRightLabel) && (
              <View style={styles.headerRight}>
                <View style={styles.headerBrandRow}>
                  {!!headerRightLeadingLogo && (
                    <Image
                      src={headerRightLeadingLogo.src}
                      style={{
                        ...(headerRightLeadingLogo.width != null ? { width: headerRightLeadingLogo.width } : {}),
                        ...(headerRightLeadingLogo.height != null ? { height: headerRightLeadingLogo.height } : {}),
                      }}
                    />
                  )}

                  {(headerRightLeadingLogo || headerRightTrailing || headerRightLogos?.length) && (
                    <View style={styles.headerDivider} />
                  )}

                  {!!headerRightLabel && <Text style={styles.headerRightLabel}>{headerRightLabel}</Text>}

                  {!!headerRightLogos?.length &&
                    headerRightLogos.map((l, idx) => (
                      <Image key={`${l.src}-${idx}`} src={l.src} style={{ width: l.width, height: l.height }} />
                    ))}

                  {!!headerRightTrailing && headerRightTrailing}
                </View>
              </View>
            )}
          </View>

          {!!filterLabel && !!filterValue && (
            <View style={styles.filterRow}>
              <View
                style={[
                  styles.filterTag,
                  {
                    borderColor: filterTagVariant === 'archived' ? '#C6C6C6' : '#2B4956',
                    backgroundColor: '#FFFFFF',
                  },
                ]}
              >
                <Text
                  style={[
                    styles.filterTagText,
                    { color: filterTagVariant === 'archived' ? '#C6C6C6' : '#2B4956' },
                  ]}
                >
                  {filterLabel}
                </Text>
              </View>
              <Text style={styles.filterValue}>{filterValue}</Text>
            </View>
          )}
        </View>

        <View style={compactTable ? styles.tableCompact : styles.table}>
          <View style={compactTable ? styles.tableHeaderCompact : styles.tableHeader}>
            {columns.map((col) => (
              <Text
                key={col.key}
                style={[
                  compactTable ? styles.tableHeaderCellCompact : styles.tableHeaderCell,
                  {
                    flex: (col.flex ?? 1) / totalFlex,
                    textAlign: getTextAlign(col.align) as any,
                  },
                ]}
              >
                {col.header}
              </Text>
            ))}
          </View>

          {rows.map((row, idx) => (
            <View key={idx} style={compactTable ? styles.tableRowCompact : styles.tableRow}>
              {columns.map((col) => (
                <Text
                  key={col.key}
                  style={[
                    compactTable ? styles.tableCellCompact : styles.tableCell,
                    {
                      flex: (col.flex ?? 1) / totalFlex,
                      textAlign: getTextAlign(col.align) as any,
                    },
                  ]}
                >
                  {row[col.key] ?? '-'}
                </Text>
              ))}
            </View>
          ))}
        </View>

        {charts && charts.length > 0 && (
          <>
            {charts.map((chart, index) => {
              // Parse description to separate main text and parenthetical text
              // Format: "Main text (parenthetical text)"
              const parseDescription = (desc: string) => {
                // Match text in parentheses: (text) - ensure no extra spaces
                const match = desc.match(/^(.+?)\s*\(\s*(.+?)\s*\)$/);
                if (match) {
                  return {
                    main: match[1].trim(),
                    parenthetical: match[2].trim().replace(/^\s+|\s+$/g, ''),
                  };
                }
                return {
                  main: desc,
                  parenthetical: null,
                };
              };

              const parsed = chart.description ? parseDescription(chart.description) : null;

              return (
                <View key={index} style={styles.chartSection}>
                  <Text style={styles.chartTitle}>{chart.title}</Text>
                  {parsed && (
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
                      <Text style={styles.chartDescription}>{parsed.main}</Text>
                      {parsed.parenthetical && (
                        <Text style={[styles.chartDescription, { marginLeft: 2 }]}>
                          {' '}
                          <Text style={styles.chartDescriptionParenthetical}>(</Text>
                          <Text style={styles.chartDescriptionParenthetical}>{parsed.parenthetical.trim()}</Text>
                          <Text style={styles.chartDescriptionParenthetical}>)</Text>
                        </Text>
                      )}
                    </View>
                  )}
                  <Image src={chart.imageSrc} style={styles.chartImage} />
                </View>
              );
            })}
          </>
        )}

        <View style={styles.footer} fixed>
          <View style={styles.footerLeft}>
            <Text style={styles.footerLeftOrg}>{organisationName ?? ''}</Text>
            <Text style={styles.footerLeftExport}>
              {exportedAtLabel}: {exportedAtValue}
            </Text>
          </View>
          <Text
            style={styles.footerRight}
            render={({ pageNumber, totalPages }) => `${pageLabel} ${pageNumber} / ${totalPages}`}
          />
        </View>
      </Page>
    </Document>
  );
};


